import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { setAppLanguage } from '@/i18n';
import { fireResellerLead, getRefCode } from '@/lib/referral';
import { setAppTimezone } from '@/lib/appTimezone';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      // Sliding session: past half its life the server hands back a fresh token — swap it in so nobody is logged out mid-use.
      if (res.data?.refreshed_token) { try { localStorage.setItem('auth_token', res.data.refreshed_token); } catch { /* ignore */ } delete res.data.refreshed_token; }
      try { localStorage.setItem('veluro_returning', '1'); } catch { /* ignore */ }
      setUser(res.data);
      setIsAuthenticated(true);
      try {
        // One app-wide timezone: the owner's saved choice, else the device clock (saved once so schedulers plan in local hours).
        const device = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const saved = res.data?.timezone;
        setAppTimezone(saved || device);
        if (device && !res.data?.timezone_manual && saved !== device && localStorage.getItem('veluro_tz_sent') !== device) {
          api.post('/auth/device-timezone', { timezone: device }).then(() => { localStorage.setItem('veluro_tz_sent', device); setAppTimezone(device); }).catch(() => {});
        }
      } catch { /* no Intl */ }
      api.get('/settings').then((s) => {
        const lang = s.data?.language;
        if (lang && lang !== (localStorage.getItem('veluro_lang') || 'en')) setAppLanguage(lang);
      }).catch(() => {});
    } catch {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/google') {
      setLoading(false);
      return;
    }
    if (window.location.pathname.startsWith('/b/')) {
      // Public brand blog — no auth check needed (avoids 401 noise for visitors/crawlers).
      setLoading(false);
      return;
    }
    // Don't block landing page with auth check
    if (window.location.pathname === '/') {
      setLoading(false);
    }
    checkAuth();
  }, [checkAuth]);

  const syncLanguage = () => {
    api.get('/settings').then((s) => {
      const lang = s.data?.language;
      if (lang && lang !== (localStorage.getItem('veluro_lang') || 'en')) setAppLanguage(lang);
    }).catch(() => {});
  };

  const login = async (email, password) => {
    // A referral code still in this browser rides along so a signup whose attribution was lost can be credited.
    const res = await api.post('/auth/login', { email, password, ref_code: getRefCode() });
    localStorage.setItem('auth_token', res.data.token);
    setUser(res.data.user);
    setIsAuthenticated(true);
    syncLanguage();
    return res.data;
  };

  const cashclubLogin = async (email, password, businessName) => {
    const res = await api.post('/cashclub/login', { email, password, business_name: businessName });
    localStorage.setItem('auth_token', res.data.token);
    setUser(res.data.user);
    setIsAuthenticated(true);
    syncLanguage();
    return res.data;
  };

  const register = async (email, password, name, refCode = null, refAsset = null, refCampaign = null, gclid = null, signupPlan = null, signupInterval = 'month') => {
    const res = await api.post('/auth/register', { email, password, name, ref_code: refCode, ref_asset: refAsset, ref_campaign: refCampaign, gclid, signup_plan: signupPlan, signup_interval: signupInterval });
    fireResellerLead();
    if (res.data.verification_required) return res.data;
    localStorage.setItem('auth_token', res.data.token);
    setUser(res.data.user);
    setIsAuthenticated(true);
    return res.data;
  };

  const verifyEmail = async (email, code) => {
    const res = await api.post('/auth/verify-email', { email, code });
    localStorage.setItem('auth_token', res.data.token);
    setUser(res.data.user);
    setIsAuthenticated(true);
    syncLanguage();
    return res.data;
  };

  const resendCode = (email) => api.post('/auth/resend-code', { email });

  const loginWithGoogle = (planOverride, intervalOverride) => {
    // Remember the plan they picked (picker or pricing-page link) so the new account records its signup intent.
    const plan = (typeof planOverride === 'string' && planOverride) || new URLSearchParams(window.location.search).get('plan');
    const interval = intervalOverride === 'year' ? 'year' : 'month';
    try { if (plan) sessionStorage.setItem('veluro_signup_plan', plan); else sessionStorage.removeItem('veluro_signup_plan'); } catch { /* ignore */ }
    try { sessionStorage.setItem('veluro_signup_interval', interval); } catch { /* ignore */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Veluro's own Google OAuth client: the backend builds the Google URL with redirect {origin}/auth/google.
    return api.get('/auth/google-login/start')
      .then((r) => { window.location.href = r.data.authorization_url; })
      .catch((e) => { window.alert(e?.response?.data?.detail || 'Google sign-in is unavailable right now — please try again.'); });
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('auth_token');
    try { localStorage.removeItem('veluro_returning'); } catch { /* ignore */ }
    setUser(null);
    setIsAuthenticated(false);
  };

  const setUserData = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated,
      login, cashclubLogin, register, verifyEmail, resendCode, loginWithGoogle, logout,
      setUserData, checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
